/**
 * Mobile Team Home Dashboard
 * Complete team dashboard with box scores, rankings, starters, and notes
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dynastyTheme, dynastyTileStyle, withAlpha } from '../../../services/colorService';
import { mobileTileStyle } from '../../../services/mobileStyleService';
import { leaguesAPI } from '../../../services/apiService';
import { formatBattingAvg, formatERA } from '../../../services/statFormatUtils';
import { MobileDynastyTable } from '../components/MobileDynastyTable';
import { buildMobileBoxScoreColumns } from '../utils/boxScoreColumns';
import { fetchBoxScore, fetchAvailableDates, fetchBoxScoreRankings } from '../../../services/boxScoreApi';
import BoxScoreCategories from '../components/BoxScoreCategories';
import StartingPitchersTile from '../components/StartingPitchersTile';
import PlayerNotesTile from '../components/PlayerNotesTile';
import MobileSectionHeader from '../components/MobileSectionHeader';
import MobileTableSectionHeader from '../components/MobileTableSectionHeader';
import MobileTeamNavigation from '../components/MobileTeamNavigation';
import MobilePageHeader from '../components/MobilePageHeader';
import MobileTeamStats from './MobileTeamStats';
import MobileRoster from './MobileRoster';
import MobileOptimizer from './optimizer/MobileOptimizer';
import MobileWatchList from './MobileWatchList';
import MobileTeamBoxScore from './MobileTeamBoxScore';
import MobileTeamSetup from './MobileTeamSetup';
import MobileFreeAgentSearch from './MobileFreeAgentSearch';
import MobileLiveDraft from './MobileLiveDraft';
import MobileTradeCenter from './MobileTradeCenter';
import MobileProbables from './MobileProbables';
import { MobilePlayerModal, MobilePlayerCard } from '../components/player';
import { useCommissioner } from '../../../contexts/CommissionerContext';
import CommissionerModeBar from '../../commissioner/CommissionerModeBar';

const C = dynastyTheme.tokens.colors;

function MobileTeamHome({ leagueId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    isCommissionerMode, 
    activeTeamId,
    isCommissioner,
    enterCommissionerMode
  } = useCommissioner();
  
  const [dashboardData, setDashboardData] = useState(null);
  const [league, setLeague] = useState(null);  // for league_settings.scoring_categories (Bible § 14)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStatsTab, setActiveStatsTab] = useState('hitting');
  const [boxScoreData, setBoxScoreData] = useState(null);
  const [boxScoreDate, setBoxScoreDate] = useState(null);
  const [boxScoreRankings, setBoxScoreRankings] = useState(null);
  const [currentPage, setCurrentPage] = useState('dashboard');
  // DD-MOBILE-OPTIMIZER-ACTIONS-0724: an optimizer package deep-linked into the Trade Center.
  const [tradeCenterPrefill, setTradeCenterPrefill] = useState(null);
  const [teamId, setTeamId] = useState(null);
  const [allTeams, setAllTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [userTeamId, setUserTeamId] = useState(null);
  const standingsCache = useRef(null);
  const pendingTargetTeamId = useRef(null);

  // Player modal/card state
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [isPlayerCardOpen, setIsPlayerCardOpen] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);

  const handleNavigate = (pageId) => {
    setCurrentPage(pageId);
    console.log('Navigating to:', pageId);
  };

  // Player click handler
  const handlePlayerClick = (mlbId) => {
    console.log('Opening player modal for MLB ID:', mlbId);
    setSelectedPlayerId(mlbId?.toString());
    setIsPlayerModalOpen(true);
  };

  // Modal expand to card
  const handleExpandToCard = () => {
    setIsPlayerModalOpen(false);
    setIsPlayerCardOpen(true);
  };

  // Card collapse to modal
  const handleCollapseToModal = () => {
    setIsPlayerCardOpen(false);
    setIsPlayerModalOpen(true);
  };

  // Close everything
  const handleClosePlayer = () => {
    setIsPlayerModalOpen(false);
    setIsPlayerCardOpen(false);
    setSelectedPlayerId(null);
  };

  // Get page display name
  const getPageName = () => {
    const pageNames = {
      'dashboard': 'Dashboard',
      'stats': 'Stats',
      'roster': 'Roster',
      'optimizer': 'Optimizer',
      'probable-pitchers': 'Probable',
      'box-scores': 'Box Scores',
      'lineup-history': 'History',
      'team-setup': 'Setup',
      'free-agents': 'Free Agents',
      'trade-center': 'Trades',
      'draft': 'Draft',
      'off-season': 'Off Season',
      'watch-list': 'Watch List',
      'minor-league': 'Minors'
    };
    return pageNames[currentPage] || 'Dashboard';
  };

  // Handle navigation state from standings modal
  useEffect(() => {
    if (location.state) {
      const { targetPage, targetTeamId } = location.state;

      // Set the target page if provided
      if (targetPage && ['roster', 'stats', 'dashboard'].includes(targetPage)) {
        setCurrentPage(targetPage);
      }

      // Store targetTeamId in ref so loadAllTeams can resolve it after teams load
      if (targetTeamId) {
        pendingTargetTeamId.current = targetTeamId;
      }

      // Clear the navigation state to prevent re-triggering on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (leagueId) {
      // Load teams first (fast, needed for header + nav)
      loadAllTeams();
      // Load box scores (independent, fast)
      fetchYesterdayBoxScore();
      // Fetch league details so league_settings.scoring_categories drives the
      // BoxScoreCategories tile set (Bible § 14).
      leaguesAPI.getLeagueDetails(leagueId)
        .then((res) => {
          if (res?.success && res.league) setLeague(res.league);
        })
        .catch(() => {});
      // Defer heavy dashboard data (starters + notes + roster) slightly
      const timer = setTimeout(() => fetchTeamDashboard(), 100);
      return () => clearTimeout(timer);
    }
  }, [leagueId]);

  // Update team_info when switching to stats page or when selectedTeamId changes
  useEffect(() => {
    if (currentPage === 'stats' && allTeams.length > 0 && dashboardData) {
      const targetTeamId = isCommissionerMode ? activeTeamId : (selectedTeamId || userTeamId);
      const selectedTeam = allTeams.find(t => t.team_id === targetTeamId);
      
      if (selectedTeam) {
        // Update dashboardData with the selected team's info
        setDashboardData(prev => ({
          ...prev,
          team_info: {
            team_name: selectedTeam.team_name,
            team_logo_url: selectedTeam.team_logo_url,
            rank: selectedTeam.rank,
            total_teams: selectedTeam.total_teams,
            total_points: selectedTeam.total_points
          }
        }));
      }
    }
  }, [currentPage, selectedTeamId, allTeams, isCommissionerMode, activeTeamId, userTeamId]);

  const loadAllTeams = async () => {
    try {
      if (!leagueId) return;
      
      const response = await leaguesAPI.getLeagueTeams(leagueId);
      
      if (response.success && response.teams) {
        // Use cached standings if available, otherwise fetch once
        let standings = [];
        if (standingsCache.current) {
          standings = standingsCache.current;
        } else {
          try {
            const standingsResponse = await leaguesAPI.getLeagueStandings(leagueId);
            if (standingsResponse.success && standingsResponse.standings) {
              standings = standingsResponse.standings;
              standingsCache.current = standings;
            }
          } catch (err) {
            console.error('Error fetching standings:', err);
          }
        }
        
        // Sort standings by total_points descending to calculate rank
        const sortedStandings = [...standings].sort(
          (a, b) => (b.total_points || b.totalPoints || 0) - (a.total_points || a.totalPoints || 0)
        );
        
        const teamList = response.teams.map((team, index) => {
          // Find team in standings to get accurate rank and points
          const standing = standings.find(s => s.team_id === team.team_id);
          const rank = standing ? sortedStandings.findIndex(s => s.team_id === team.team_id) + 1 : (index + 1);
          const totalPoints = standing ? (standing.total_points || standing.totalPoints || 0) : 0;
          
          return {
            team_id: team.team_id,
            team_name: team.team_name || 'Unnamed Team',
            manager_name: team.manager_name || 'Unknown Manager',
            is_user_team: team.is_user_team || false,
            team_logo_url: team.team_logo_url || null,
            total_points: totalPoints,
            rank: rank,
            total_teams: standings.length > 0 ? standings.length : response.teams.length
          };
        });
        
        setAllTeams(teamList);
        
        const userTeam = teamList.find(t => t.is_user_team);
        if (userTeam) {
          setUserTeamId(userTeam.team_id);
        }
        
        // Apply pending target team ID if provided, otherwise use user team
        const targetId = pendingTargetTeamId.current;
        if (targetId) {
          pendingTargetTeamId.current = null;
          const targetTeam = teamList.find(t => t.team_id === targetId);
          if (targetTeam) {
            setSelectedTeamId(targetId);
          }
          // Don't fall back to user team when a target was explicitly specified
        } else if (userTeam && !selectedTeamId && !isCommissionerMode) {
          // Default to user team if no navigation state
          setSelectedTeamId(userTeam.team_id);
        }
      }
    } catch (err) {
      console.error('Error loading teams:', err);
    }
  };

  const handleTeamChange = async (e) => {
    const newTeamId = e.target.value;
    setSelectedTeamId(newTeamId);
    
    // Update team_info from allTeams array
    if (currentPage === 'stats' && allTeams.length > 0) {
      const selectedTeam = allTeams.find(t => t.team_id === newTeamId);
      if (selectedTeam && dashboardData) {
        // Update dashboardData with the selected team's info
        setDashboardData(prev => ({
          ...prev,
          team_info: {
            team_name: selectedTeam.team_name,
            team_logo_url: selectedTeam.team_logo_url,
            rank: selectedTeam.rank,
            total_teams: selectedTeam.total_teams,
            total_points: selectedTeam.total_points
          }
        }));
      }
    } else if (leagueId) {
      // For dashboard, reload dashboard data
      fetchTeamDashboard();
    }
  };

  const fetchYesterdayBoxScore = async () => {
    try {
      // Get team ID first
      const teamResponse = await leaguesAPI.getUserTeam(leagueId);
      const fetchedTeamId = teamResponse?.team_id;
      
      if (!fetchedTeamId) {
        console.log('No team ID found');
        return;
      }
      
      setTeamId(fetchedTeamId);

      // Get available dates
      // Don't pass season_year — let the backend use the league's current_season
      const datesResponse = await fetchAvailableDates(leagueId, fetchedTeamId);
      
      if (datesResponse.success && datesResponse.dates && datesResponse.dates.length > 0) {
        const mostRecentDate = datesResponse.dates[0];
        console.log(`📊 Fetching box score for: ${mostRecentDate}`);
        setBoxScoreDate(mostRecentDate);
        
        // Fetch box score
        const response = await fetchBoxScore(leagueId, fetchedTeamId, mostRecentDate);
        
        if (response.success && response.box_score) {
          setBoxScoreData({
            hitting: response.box_score.hitting || [],
            pitching: response.box_score.pitching || [],
            team_totals: response.box_score.team_totals || {},
            summary_stats: response.box_score.summary_stats || {}
          });
        }
        
        // Fetch rankings - store full response (includes your_ranks AND all_rankings)
        try {
          const rankingsResponse = await fetchBoxScoreRankings(leagueId, fetchedTeamId, mostRecentDate);
          if (rankingsResponse.success && rankingsResponse.your_ranks) {
            console.log('🏆 Rankings loaded:', rankingsResponse.your_ranks);
            // Store the full response (includes your_ranks AND all_rankings)
            setBoxScoreRankings(rankingsResponse);
          }
        } catch (rankingsError) {
          console.error('❌ Rankings error:', rankingsError);
        }
      }
    } catch (err) {
      console.error('Failed to load box score:', err);
    }
  };

  const fetchTeamDashboard = async () => {
    try {
      setLoading(true);
      const data = await leaguesAPI.getTeamHomeData(leagueId);
      setDashboardData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching team dashboard:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        padding: '20px',
        paddingBottom: '0',
          backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: `3px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
            borderTop: `3px solid ${dynastyTheme.tokens.colors.primary}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 15px'
          }}></div>
          <p style={{ color: dynastyTheme.tokens.colors.neutral[400] }}>
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        padding: '20px',
        paddingBottom: '0',
          backgroundColor: 'transparent'
      }}>
        <div style={{
          padding: '20px',
          backgroundColor: dynastyTheme.tokens.colors.error + '20',
          border: `1px solid ${dynastyTheme.tokens.colors.error}`,
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p style={{ color: dynastyTheme.tokens.colors.error, marginBottom: '15px' }}>
            ❌ {error}
          </p>
          <button
            onClick={fetchTeamDashboard}
            style={{
              padding: '10px 20px',
              backgroundColor: dynastyTheme.tokens.colors.primary,
              color: C.black,
              border: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  // Get team info - use dashboard data which has full team info
  const team_info = dashboardData?.team_info || {};
  
  const {
  roster_stats = [],
  starting_pitchers = [],
  player_notes = [],
    last_night_box = { hitting: [], pitching: [] }
  } = dashboardData || {};
  
  const safeRosterStats = Array.isArray(roster_stats) ? roster_stats : [];
  const safeStartingPitchers = Array.isArray(starting_pitchers) ? starting_pitchers : [];
  const safePlayerNotes = Array.isArray(player_notes) ? player_notes : [];

  // Filter stats by type
  const hitters = safeRosterStats.filter(p =>
    ['C', '1B', '2B', '3B', 'SS', 'OF', 'DH', 'UTIL'].includes(p.info?.position)
  );
  const pitchers = safeRosterStats.filter(p =>
    ['SP', 'RP'].includes(p.info?.position)
  );

  // Use pre-calculated team totals from backend BoxScoreService
  const bHitting = boxScoreData?.team_totals?.hitting || {};
  const bPitching = boxScoreData?.team_totals?.pitching || {};

  const hittingTotals = {
    ab: bHitting.at_bats || 0,
    r: bHitting.runs || 0,
    h: bHitting.hits || 0,
    hr: bHitting.home_runs || 0,
    rbi: bHitting.rbi || 0,
    sb: bHitting.stolen_bases || 0,
    bb: bHitting.walks || 0,
    so: bHitting.strikeouts || 0,
    avg: formatBattingAvg(bHitting.avg) || '.000',
    ops: formatBattingAvg(bHitting.ops) || '.000'
  };

  const pitchingTotals = {
    w: bPitching.wins || 0,
    sv: bPitching.saves || 0,
    ip: (bPitching.innings_pitched || 0).toFixed(1),
    h: bPitching.hits_allowed || 0,
    er: bPitching.earned_runs || 0,
    bb: bPitching.walks_allowed || 0,
    so: bPitching.strikeouts_pitched || 0,
    qs: bPitching.quality_starts || 0,
    era: formatERA(bPitching.era) || '0.00',
    whip: formatBattingAvg(bPitching.whip) || '.000'
  };

  return (
    <div style={{
      backgroundColor: 'transparent'
    }}>
      {/* Horizontal Navigation */}
      <MobileTeamNavigation 
        currentPage={currentPage}
        onNavigate={handleNavigate}
      />

      {/* Dashboard header — LOCKED nav-system structure: TeamChrome → 4px →
          static header-tile.jpg banner with pts · standing overlaid (no olive
          tint, no separate context line, no page-badge overlay), same correction
          as Live Scoring. Other team pages keep MobilePageHeader untouched. */}
      {currentPage === 'dashboard' ? (
        <div style={{ marginBottom: '4px', position: 'relative' }}>
          <img src="/header-tile.jpg" alt="" style={{ width: '100%', display: 'block' }} />
          {team_info.total_points != null && (
            <div style={{ position: 'absolute', right: 10, bottom: 8, display: 'flex', alignItems: 'baseline', gap: 6, textShadow: `0 1px 4px ${withAlpha(C.black, 0.85)}`, fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ fontFamily: "'Lato', sans-serif", fontSize: 18, fontWeight: 900, color: dynastyTheme.tokens.colors.primary, lineHeight: 1 }}>
                {team_info.total_points.toFixed(1)}
              </span>
              <span style={{ fontSize: 10, fontWeight: 700, color: C.coolGray[150] }}>pts</span>
              {team_info.rank && (
                <span style={{ fontSize: 11, fontWeight: 800, color: C.coolGray[150] }}>
                  {`· ${team_info.rank}${team_info.rank === 1 ? 'st' : team_info.rank === 2 ? 'nd' : team_info.rank === 3 ? 'rd' : 'th'} / ${team_info.total_teams}`}
                </span>
              )}
            </div>
          )}
        </div>
      ) : (
      <MobilePageHeader
        title={team_info.team_name || 'Your Team'}
        subtitle={team_info.total_points != null ? `${team_info.total_points.toFixed(1)} pts${team_info.rank ? ` · ${team_info.rank}${team_info.rank === 1 ? 'st' : team_info.rank === 2 ? 'nd' : team_info.rank === 3 ? 'rd' : 'th'} / ${team_info.total_teams}` : ''}` : null}
        pageBadge={getPageName()}
        logoUrl={team_info.team_logo_url}
        logoFallbackText={team_info.team_name}
        inlineDropdown={!isCommissionerMode && allTeams.length > 1 && (currentPage === 'stats' || currentPage === 'roster' || currentPage === 'box-scores') ? (
          <select
            value={selectedTeamId || userTeamId || ''}
            onChange={handleTeamChange}
            style={{
              width: '100%', padding: '2px 4px',
              backgroundColor: dynastyTheme.tokens.colors.neutral[700],
              color: dynastyTheme.tokens.colors.white,
              border: `1px solid ${dynastyTheme.tokens.colors.neutral[600]}`,
              borderRadius: '3px', fontSize: '10px',
            }}
          >
            {allTeams.map(team => (
              <option key={team.team_id} value={team.team_id}>
                {team.team_name}{team.is_user_team ? ' (You)' : ` (${team.manager_name})`}
              </option>
            ))}
          </select>
        ) : null}
      />
      )}

      {/* Main Content with Padding */}
      <div style={{ padding: '0 8px 0 8px' }}>
      
      {/* Commissioner Mode Bar - Only show on Stats page */}
      {currentPage === 'stats' && isCommissionerMode && dashboardData && (
        <div style={{ padding: '0 15px', marginBottom: '10px' }}>
          <CommissionerModeBar 
            league={{ league_id: leagueId }}
            onTeamSwitch={() => {
              if (leagueId) {
                fetchTeamDashboard();
              }
            }}
          />
        </div>
      )}

      {/* Daily Category Rankings — flush, no card (left-rule header) */}
      {currentPage === 'dashboard' && boxScoreData && (boxScoreData.hitting.length > 0 || boxScoreData.pitching.length > 0) && (
        // 6px below the rankings tiles + 6px Box-Score header top-padding = 12px
        // top gap above the Box Score label (matches the Probable Pitchers ref).
        <div>
          <MobileSectionHeader title="Daily Category Rankings" />

          <BoxScoreCategories
            league={league}
            rankings={boxScoreRankings?.your_ranks || null}
            hittingTotals={hittingTotals}
            pitchingTotals={pitchingTotals}
            allRankings={boxScoreRankings?.all_rankings || null}
            yourTeamId={teamId}
            statsDate={boxScoreRankings?.stats_date || boxScoreDate || ''}
            weeklyHistory={boxScoreRankings?.your_team_weekly_history || null}
            wow={boxScoreRankings?.your_team_wow || null}
            weekly={boxScoreRankings?.your_team_weekly || null}
            lastWeek={boxScoreRankings?.your_team_last_week || null}
            season={boxScoreRankings?.your_team_season || null}
            weekMeta={boxScoreRankings?.week_meta || null}
          />
        </div>
      )}

      {/* Box Score (BoxMini) — flush, no card; house Game-Logs zebra (the shared
          MobileDynastyTable primitive), frozen POS+Player, team totals. */}
      {currentPage === 'dashboard' && boxScoreData && (boxScoreData.hitting.length > 0 || boxScoreData.pitching.length > 0) && (
        <div>
          {/* Box Score header — lifted to the shared MobileTableSectionHeader so
              Dashboard/Stats/Roster share one pattern: green (success) accent bar
              + gold (primary) "BOX SCORE · <date>" label + INLINE right-aligned
              Hitters/Pitchers tabs. */}
          <MobileTableSectionHeader
            label={`Box Score${boxScoreDate ? ` · ${new Date(boxScoreDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}`}
            color={dynastyTheme.tokens.colors.primary}
            barColor={dynastyTheme.tokens.colors.primary}
            tabs={{
              active: activeStatsTab,
              onSelect: setActiveStatsTab,
              options: [
                { key: 'hitting', label: `Hitters ${boxScoreData.hitting.length}` },
                { key: 'pitching', label: `Pitchers ${boxScoreData.pitching.length}` },
              ],
            }}
          />

          {/* Table — shared MobileDynastyTable primitive. Columns are
              scoring-derived via buildMobileBoxScoreColumns (Bible § 14),
              matching the tile + full box-score page; totals are client-side
              (TeamHome receives no backend team_totals). */}
          {(() => {
            const rows = activeStatsTab === 'hitting' ? boxScoreData.hitting : boxScoreData.pitching;
            const columns = buildMobileBoxScoreColumns({
              scoringCategories: league?.league_settings?.scoring_categories,
              activeTab: activeStatsTab,
              includeTeamColumn: true,
              onPlayerClick: handlePlayerClick,
            });
            const reduced = rows.reduce((acc, player) => {
              const stats = activeStatsTab === 'hitting' ? player.stats?.hitting || {} : player.stats?.pitching || {};
              if (activeStatsTab === 'hitting') {
                return {
                  ab: (acc.ab || 0) + (stats.at_bats || 0),
                  r: (acc.r || 0) + (stats.runs || 0),
                  h: (acc.h || 0) + (stats.hits || 0),
                  hr: (acc.hr || 0) + (stats.home_runs || 0),
                  rbi: (acc.rbi || 0) + (stats.rbi || 0),
                  sb: (acc.sb || 0) + (stats.stolen_bases || 0),
                  bb: (acc.bb || 0) + (stats.walks || 0),
                  so: (acc.so || 0) + (stats.strikeouts || 0),
                  // OBP/SLG components (box_score_service aggregates these per
                  // player) — summed so team OPS = team OBP + team SLG.
                  doubles: (acc.doubles || 0) + (stats.doubles || 0),
                  triples: (acc.triples || 0) + (stats.triples || 0),
                  hbp: (acc.hbp || 0) + (stats.hit_by_pitch || 0),
                  sf: (acc.sf || 0) + (stats.sacrifice_flies || 0),
                };
              }
              return {
                w: (acc.w || 0) + (stats.wins || 0),
                l: (acc.l || 0) + (stats.losses || 0),
                sv: (acc.sv || 0) + (stats.saves || 0),
                ip: (acc.ip || 0) + (stats.innings_pitched || 0),
                h: (acc.h || 0) + (stats.hits_allowed || 0),
                er: (acc.er || 0) + (stats.earned_runs || 0),
                bb: (acc.bb || 0) + (stats.walks_allowed || 0),
                so: (acc.so || 0) + (stats.strikeouts || 0),
                qs: (acc.qs || 0) + (stats.quality_starts || 0),
              };
            }, {});
            // Team OPS = team OBP + team SLG, derived from summed components
            // (NOT an average of per-player OPS). Mirrors box_score_service.
            const teamPa = (reduced.ab || 0) + (reduced.bb || 0) + (reduced.hbp || 0) + (reduced.sf || 0);
            const teamObp = teamPa > 0 ? ((reduced.h || 0) + (reduced.bb || 0) + (reduced.hbp || 0)) / teamPa : 0;
            const teamTb = (reduced.h || 0) + (reduced.doubles || 0) + 2 * (reduced.triples || 0) + 3 * (reduced.hr || 0);
            const teamSlg = reduced.ab > 0 ? teamTb / reduced.ab : 0;
            const currentTotals = activeStatsTab === 'hitting'
              ? {
                  ...reduced,
                  avg: reduced.ab > 0 ? formatBattingAvg(reduced.h / reduced.ab) : '.000',
                  obp: formatBattingAvg(teamObp),
                  slg: formatBattingAvg(teamSlg),
                  ops: formatBattingAvg(teamObp + teamSlg),
                }
              : { ...reduced, ip: (reduced.ip || 0).toFixed(1), era: reduced.ip > 0 ? formatERA((reduced.er * 9) / reduced.ip) : '0.00' };
            return (
              <div>
                <MobileDynastyTable
                  data={rows}
                  columns={columns}
                  keyField="player_id"
                  tableType="player-list"
                  showTotals={rows.length > 0}
                  totalsRow={currentTotals}
                  rowDivider={`1px solid ${withAlpha(dynastyTheme.tokens.colors.goldBase, 0.5)}`}
                />
              </div>
            );
          })()}

          {(activeStatsTab === 'hitting' ? boxScoreData.hitting : boxScoreData.pitching).length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: dynastyTheme.tokens.colors.neutral[500]
            }}>
              No {activeStatsTab} data for this date
            </div>
          )}
        </div>
      )}

      {/* Trade Center */}
      {currentPage === 'trade-center' && (
        <MobileTradeCenter leagueId={leagueId} userTeamId={userTeamId}
          initialPackage={tradeCenterPrefill}
          onPrefillConsumed={() => setTradeCenterPrefill(null)} />
      )}

      {/* Other Pages - Placeholder. Exclusion list MUST include every
          currentPage value that has an explicit handler below so the
          placeholder doesn't render stacked behind a real page. */}
      {currentPage !== 'dashboard' && currentPage !== 'stats' && currentPage !== 'roster' && currentPage !== 'box-scores' && currentPage !== 'team-setup' && currentPage !== 'free-agents' && currentPage !== 'trade-center' && currentPage !== 'draft' && currentPage !== 'probable-pitchers' && currentPage !== 'watch-list' && currentPage !== 'optimizer' && (
        <div style={{
          ...mobileTileStyle,
          padding: '40px',
          marginTop: '20px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '20px'
          }}>
            🚧
          </div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: C.white,
            marginBottom: '10px'
          }}>
            {getPageName()}
          </h2>
          <p style={{
            fontSize: '14px',
            color: dynastyTheme.tokens.colors.neutral[400]
          }}>
            This page is under construction
          </p>
        </div>
      )}

      {/* Stats Page */}
      {currentPage === 'stats' && (
        <MobileTeamStats 
          leagueId={leagueId}
          selectedTeamId={isCommissionerMode ? activeTeamId : (selectedTeamId || userTeamId)}
        />
      )}

      {/* Roster Page */}
      {currentPage === 'roster' && (
        <MobileRoster leagueId={leagueId} selectedTeamId={isCommissionerMode ? activeTeamId : (selectedTeamId || userTeamId)} />
      )}

      {currentPage === 'optimizer' && (
        <MobileOptimizer leagueId={leagueId} teamId={isCommissionerMode ? activeTeamId : (selectedTeamId || userTeamId)}
          onPlayerClick={handlePlayerClick}
          onOpenTradeCenter={(pkg) => { setTradeCenterPrefill(pkg); setCurrentPage('trade-center'); }} />
      )}

      {currentPage === 'watch-list' && (
        <MobileWatchList leagueId={leagueId} league={league} selectedTeamId={isCommissionerMode ? activeTeamId : (selectedTeamId || userTeamId)} />
      )}

      {/* Box Scores Page — team driven by the banner selector (inlineDropdown),
          same pattern as Stats/Roster. */}
      {currentPage === 'box-scores' && (
        <MobileTeamBoxScore leagueId={leagueId} initialTeamId={isCommissionerMode ? activeTeamId : (selectedTeamId || userTeamId)} />
      )}

      {/* Team Setup Page */}
      {currentPage === 'team-setup' && (
        <MobileTeamSetup
          leagueId={leagueId}
          userTeam={team_info}
          onTeamUpdated={fetchTeamDashboard}
          user={{ given_name: team_info.owner_name }}
        />
      )}

      {/* Free Agents Page */}
      {currentPage === 'free-agents' && (
        <MobileFreeAgentSearch
          leagueId={leagueId}
          league={{ league_id: leagueId, role: isCommissioner ? 'commissioner' : 'owner' }}
          user={{ id: userTeamId }}
        />
      )}

      {/* Live Draft Page */}
      {currentPage === 'draft' && (
        <MobileLiveDraft leagueId={leagueId} />
      )}

      {/* Probable Pitchers Page (Team Home nav entry). MobileProbables
          resolves leagueId/teamId internally via useDefaultLeague — no
          props from Team Home context needed. */}
      {currentPage === 'probable-pitchers' && (
        <MobileProbables />
      )}

      {/* Probable Pitchers + Player Notes (dashboard) — each tile owns its
          DashFooter left-rule header now (no wrapper header here, avoids doubling). */}
      {currentPage === 'dashboard' && (
      <div style={{ marginBottom: 0, paddingBottom: 0 }}>
        <StartingPitchersTile
          starters={safeStartingPitchers}
          rosterPlayers={safeRosterStats}
          onPlayerClick={handlePlayerClick}
          onGameClick={(gameId) => {
            navigate(`/leagues/${leagueId}`, { state: { initialPage: 'live-scoring', initialGameId: gameId } });
          }}
        />
        <PlayerNotesTile
          notes={safePlayerNotes}
          leagueId={leagueId}
          onPlayerClick={handlePlayerClick}
        />
      </div>

      )}

      {/* Add keyframes for spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      </div>

      {/* PLAYER MODAL */}
      <MobilePlayerModal
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isPlayerModalOpen}
        onClose={handleClosePlayer}
        onExpandToCard={handleExpandToCard}
      />

      {/* PLAYER CARD */}
      <MobilePlayerCard
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isPlayerCardOpen}
        onClose={handleClosePlayer}
        onCollapse={handleCollapseToModal}
      />
    </div>
  );
}

export default MobileTeamHome;
